import { useState } from 'react';

const HUBSPOT_PORTAL_ID = process.env.REACT_APP_HUBSPOT_PORTAL_ID;
const HUBSPOT_FORM_ID = process.env.REACT_APP_HUBSPOT_FORM_ID;

// Format phone as "+CC AAA XXXXXXX" (default country code 92 if user types raw digits).
const maskPhone = (value) => {
  const hasPlus = value.trim().startsWith('+');
  const digits = value.replace(/\D/g, '').slice(0, 15);
  if (!digits) return hasPlus ? '+' : '';
  const cc = digits.slice(0, 2);
  const area = digits.slice(2, 5);
  const rest = digits.slice(5, 12);
  let out = `+${cc}`;
  if (area) out += ` ${area}`;
  if (rest) out += ` ${rest}`;
  return out;
};

// Email: lowercase, no whitespace, restrict to characters valid in an email local/domain.
const maskEmail = (value) =>
  value.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9._%+\-@]/g, '');

export default function CTA() {
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const [errorMsg, setErrorMsg] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === 'sending') return;

    const form = e.currentTarget;
    const fd = new FormData(form);
    const firstname = (fd.get('firstname') || '').toString().trim();
    const lastname = (fd.get('lastname') || '').toString().trim();
    const company = (fd.get('company') || '').toString().trim();
    const email = (fd.get('email') || '').toString().trim();
    const phone = (fd.get('phone') || '').toString().trim();
    const locations = (fd.get('locations') || '').toString();
    const message = (fd.get('message') || '').toString().trim();

    if (!HUBSPOT_PORTAL_ID || !HUBSPOT_FORM_ID) {
      setStatus('error');
      setErrorMsg('Form is not configured. Please try again later.');
      return;
    }

    setStatus('sending');
    setErrorMsg('');

    try {
      const params = new URLSearchParams();
      params.set('firstname', firstname);
      params.set('lastname', lastname);
      params.set('email', email);
      params.set('phone', phone);
      params.set('company', company);
      params.set('number_of_locations', locations);
      params.set('message', message);
      params.set(
        'hs_context',
        JSON.stringify({
          pageUri: typeof window !== 'undefined' ? window.location.href : '',
          pageName: 'Plateera — Request Access',
        })
      );

      const res = await fetch(
        `https://forms.hubspot.com/uploads/form/v2/${HUBSPOT_PORTAL_ID}/${HUBSPOT_FORM_ID}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: params.toString(),
        }
      );

      if (!res.ok) {
        const text = await res.text().catch(() => '');
        let msg = `Submission failed (${res.status}).`;
        try {
          const body = JSON.parse(text);
          msg = body?.errors?.[0]?.message || body?.message || msg;
        } catch (_) {}
        throw new Error(msg);
      }

      setStatus('sent');
      form.reset();
      setEmail('');
      setPhone('');
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message || 'Something went wrong. Please email us instead.');
    }
  };

  const sent = status === 'sent';
  const sending = status === 'sending';

  return (
    <section className="cta" id="cta">
      <div className="cta-inner">
        <div className="kicker reveal">
          <span className="kicker-dot" />
          <span>Early access</span>
        </div>
        <h2 className="display cta-title">
          The waitlist
          <br />
          is <em>short.</em>
          <br />
          The kitchens
          <br />
          are <em>real.</em>
        </h2>
        <p className="lede reveal">
          We onboard a small cohort of operators each quarter. Tell us about your
          restaurant — we'll be in touch within a week.
        </p>

        <form className={`cta-form${sent ? ' sent' : ''}`} onSubmit={handleSubmit}>
          <div className="cta-row">
            <label>
              <span className="cta-l">01 — First name</span>
              <input name="firstname" type="text" placeholder="Maria" required />
            </label>
            <label>
              <span className="cta-l">02 — Last name</span>
              <input name="lastname" type="text" placeholder="Pereira" required />
            </label>
          </div>
          <div className="cta-row">
            <label>
              <span className="cta-l">03 — Email</span>
              <input
                name="email"
                type="email"
                placeholder="maria@kinetic.co"
                required
                autoComplete="email"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(maskEmail(e.target.value))}
              />
            </label>
            <label>
              <span className="cta-l">04 — Phone</span>
              <input
                name="phone"
                type="tel"
                placeholder="+92 300 1234567"
                required
                autoComplete="tel"
                inputMode="tel"
                maxLength={16}
                value={phone}
                onChange={(e) => setPhone(maskPhone(e.target.value))}
              />
            </label>
          </div>
          <div className="cta-row">
            <label>
              <span className="cta-l">05 — Restaurant</span>
              <input name="company" type="text" placeholder="Kinetic Restaurant" required />
            </label>
            <label>
              <span className="cta-l">06 — Locations</span>
              <select name="locations" required defaultValue="">
                <option value="">Choose…</option>
                <option>1 restaurant</option>
                <option>2–5 restaurants</option>
                <option>6–20 restaurants</option>
                <option>20+ restaurants</option>
              </select>
            </label>
          </div>
          <div className="cta-row cta-row-full">
            <label>
              <span className="cta-l">07 — Message</span>
              <textarea
                name="message"
                rows="4"
                placeholder="Tell us a bit about your restaurant, timeline, or anything else we should know."
              />
            </label>
          </div>
          <button
            type="submit"
            className="btn btn-primary cta-submit"
            disabled={sending || sent}
          >
            <span className="lbl-default">
              {sending ? 'Sending…' : 'Send request'}
            </span>
            <span className="lbl-sent">Sent — we'll be in touch</span>
            <svg viewBox="0 0 14 14" width="14" height="14">
              <path
                d="M2 7h10M7 2l5 5-5 5"
                stroke="currentColor"
                strokeWidth="1.5"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          {status === 'error' && (
            <div className="cta-error" role="alert">
              {errorMsg} You can also reach us at{' '}
              <a href="mailto:contact@plateera.com">contact@plateera.com</a>.
            </div>
          )}
          <div className="cta-fine">
            By submitting, you agree to receive emails about Plateera onboarding.
            Unsubscribe anytime. Prefer email? Reach us at{' '}
            <a href="mailto:contact@plateera.com">contact@plateera.com</a>.
          </div>
        </form>
      </div>
    </section>
  );
}
